import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/common/components/dialog';

import {
  useRenameApiKeyMutation,
  ApiKeyRow,
} from '@common-reducers/backend-api/backend-api';
import { namespace } from '@common/config/constants';

interface Props {
  row: ApiKeyRow | null;
  onOpenChange: (open: boolean) => void;
}

export function RenameApiKeyDialog({ row, onOpenChange }: Props) {
  const { t } = useTranslation(namespace);
  const [name, setName] = useState('');
  const [rename, { isLoading }] = useRenameApiKeyMutation();

  useEffect(() => {
    if (row) setName(row.name);
  }, [row?._id]);

  const submit = async () => {
    if (!row) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === row.name) {
      onOpenChange(false);
      return;
    }
    try {
      await rename({ id: row._id, name: trimmed }).unwrap();
      onOpenChange(false);
    } catch {
      toast.error(t('api.keys.renameDialog.error'));
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('api.keys.renameDialog.title')}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-2">
          <Label htmlFor="api-key-rename">
            {t('api.keys.createDialog.nameLabel')}
          </Label>
          <Input
            id="api-key-rename"
            value={name}
            autoFocus
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) submit();
            }}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={isLoading || !name.trim()}
            className="bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/90 border-transparent"
          >
            {t('api.keys.renameDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
